import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import './Admin.css';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtNum  = (n) => (n ?? 0).toLocaleString('fr-FR');

const PLAN_META = {
  starter: { label: 'Starter', color: '#6B7280', bg: '#F3F4F6' },
  pro:     { label: 'Pro',     color: '#5A35FF', bg: '#EEE9FF' },
  premium: { label: 'Premium', color: '#D97706', bg: '#FEF3C7' },
};
const PLAN_PRICES = { starter: 49, pro: 149, premium: 299 };

function PlanBadge({ plan }) {
  const m = PLAN_META[plan] || PLAN_META.starter;
  return <span className="adm-badge" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
}
function StatusPill({ active }) {
  return (
    <span className={`adm-pill ${active ? 'adm-pill-on' : 'adm-pill-off'}`}>
      <span className="adm-pill-dot" />{active ? 'Actif' : 'Suspendu'}
    </span>
  );
}
function UsageBar({ used, limit, color = '#5A35FF' }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const warn = pct >= 90;
  return (
    <div className="adm-bar-wrap">
      <div className="adm-bar-track">
        <div className="adm-bar-fill" style={{ width: `${pct}%`, background: warn ? '#EF4444' : color }} />
      </div>
      <span className="adm-bar-label">{fmtNum(used)} / {fmtNum(limit)} ({pct}%)</span>
    </div>
  );
}
function Spinner() { return <span className="adm-spinner" />; }

// ── Barre de statut des services plateforme ───────────────────────────────────
const PLATFORM_SERVICES = [
  { key: 'ai',        label: 'IA',        icon: '🤖' },
  { key: 'sms',       label: 'SMS',       icon: '📱' },
  { key: 'whatsapp',  label: 'WhatsApp',  icon: '💬' }, // intégration plateforme Meta
  { key: 'mailjet',   label: 'Mailjet',   icon: '✉️' },
];

function PlatformStatusBar() {
  const [services, setServices] = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.getIntegrations()
      .then(res => setServices(res || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="adm-status-bar">
      <span className="adm-status-bar-label">Services plateforme :</span>
      {PLATFORM_SERVICES.map(({ key, label, icon }) => {
        const s = services[key];
        const status = !s ? 'missing' : !s.active ? 'inactive' : s.test_status;

        const pill = {
          ok:       { cls: 'ok',    text: '✅ Connecté'  },
          error:    { cls: 'error', text: '❌ Erreur'    },
          untested: { cls: 'warn',  text: '⚡ Non testé' },
          inactive: { cls: 'off',   text: '⏸ Inactif'  },
          missing:  { cls: 'off',   text: '— Non config' },
        }[status] || { cls: 'warn', text: '⚡ Inconnu' };

        const tooltip = s?.test_message || '';

        return (
          <div key={key} className={`adm-svc-pill adm-svc-pill-${pill.cls}`} title={tooltip}>
            <span className="adm-svc-icon">{icon}</span>
            <span className="adm-svc-name">{label}</span>
            <span className="adm-svc-status">{pill.text}</span>
            {status === 'error' && tooltip && (
              <span className="adm-svc-error-tip" title={tooltip}>ⓘ</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Modal détail tenant ────────────────────────────────────────────────────────
function TenantModal({ tenantId, onClose, onRefresh }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('info');
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');

  // Limites éditables
  const [limits, setLimits] = useState({});
  // Crédits
  const [creditType, setCreditType]     = useState('sms');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditSaving, setCreditSaving] = useState(false);
  // Reset usage
  const [resetType, setResetType]   = useState('all');
  const [resetSaving, setResetSaving] = useState(false);

  const [delOpen, setDelOpen]     = useState(false);
  const [delConfirm, setDelConfirm] = useState('');
  const [delBusy, setDelBusy]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminTenant(tenantId);
      setData(res.tenant);
      setLimits({
        contacts_limit:   res.tenant.contacts_limit,
        emails_limit:   res.tenant.emails_limit,
        ai_limit:       res.tenant.ai_limit,
        sms_limit:      res.tenant.sms_limit,
        whatsapp_limit: res.tenant.whatsapp_limit,
      });
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const handleSaveLimits = async () => {
    setSaving(true);
    try {
      await api.updateAdminTenant(tenantId, limits);
      flash('✅ Limites sauvegardées');
      load(); onRefresh();
    } catch { flash('❌ Erreur'); }
    finally { setSaving(false); }
  };

  const handleToggle = async () => {
    setSaving(true);
    try {
      await api.updateAdminTenant(tenantId, { active: !data.active });
      flash(data.active ? '⏸ Client suspendu' : '✅ Client activé');
      load(); onRefresh();
    } finally { setSaving(false); }
  };

  const handlePlanChange = async (plan) => {
    setSaving(true);
    try {
      await api.updateAdminTenant(tenantId, { plan });
      flash('✅ Plan mis à jour');
      load(); onRefresh();
    } finally { setSaving(false); }
  };

  const handleAddCredits = async () => {
    if (!creditAmount || creditAmount <= 0) return;
    setCreditSaving(true);
    try {
      await api.addAdminCredits(tenantId, { type: creditType, amount: parseInt(creditAmount) });
      flash(`✅ ${creditAmount} crédits ${creditType.toUpperCase()} ajoutés`);
      setCreditAmount('');
      load(); onRefresh();
    } catch { flash('❌ Erreur'); }
    finally { setCreditSaving(false); }
  };

  const handleResetUsage = async () => {
    if (!window.confirm(`Réinitialiser les compteurs "${resetType}" pour ce client ?`)) return;
    setResetSaving(true);
    try {
      await api.resetAdminUsage(tenantId, { type: resetType });
      flash('✅ Compteurs réinitialisés');
      load(); onRefresh();
    } finally { setResetSaving(false); }
  };

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.deleteAdminTenant(tenantId, delConfirm);
      onRefresh();
      onClose();
    } catch (e) {
      flash(e?.message || '❌ Suppression impossible (nom incorrect ?)');
    } finally { setDelBusy(false); }
  };

  // ── WhatsApp Business (support admin) ────────────────────────────────────
  const [waForm, setWaForm]       = useState({ phone_number_id: '', access_token: '' });
  const [waSaving, setWaSaving]   = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waTestRes, setWaTestRes] = useState(null);

  useEffect(() => {
    if (data?.settings?.whatsapp?.phone_number_id) {
      setWaForm(f => ({ ...f, phone_number_id: data.settings.whatsapp.phone_number_id }));
    }
  }, [data]);

  const handleSaveWa = async () => {
    if (!waForm.phone_number_id) return;
    setWaSaving(true);
    try {
      await api.adminSaveTenantWhatsApp(tenantId, waForm);
      setWaForm(f => ({ ...f, access_token: '' }));
      flash('✅ WhatsApp configuré');
      load();
    } catch { flash('❌ Erreur lors de la sauvegarde'); }
    finally { setWaSaving(false); }
  };

  const handleTestWa = async () => {
    setWaTesting(true); setWaTestRes(null);
    try {
      const res = await api.adminTestTenantWhatsApp(tenantId);
      setWaTestRes(res);
      if (res.ok) { flash(`✅ Connecté : ${res.display_name}`); load(); }
    } catch { setWaTestRes({ ok: false, message: 'Erreur de connexion' }); }
    finally { setWaTesting(false); }
  };

  const handleDeleteWa = async () => {
    if (!window.confirm(`Déconnecter WhatsApp de ${data.name} ?`)) return;
    try {
      await api.adminDeleteTenantWhatsApp(tenantId);
      setWaForm({ phone_number_id: '', access_token: '' });
      setWaTestRes(null);
      flash('WhatsApp déconnecté');
      load();
    } catch { flash('❌ Erreur'); }
  };

  return (
    <div className="adm-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="adm-modal">
        {loading || !data ? (
          <div className="adm-modal-loading"><Spinner /> Chargement…</div>
        ) : (
          <>
            {/* Header */}
            <div className="adm-modal-header">
              <div>
                <div className="adm-modal-title">{data.name}</div>
                <div className="adm-modal-sub">
                  <PlanBadge plan={data.plan} />
                  <StatusPill active={data.active} />
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>Inscrit le {fmtDate(data.created_at)}</span>
                </div>
              </div>
              <button className="adm-modal-close" onClick={onClose}>✕</button>
            </div>

            {msg && <div className="adm-flash">{msg}</div>}

            {/* Tabs */}
            <div className="adm-modal-tabs">
              {[
                { key: 'info',     label: '📋 Infos' },
                { key: 'usage',    label: '📊 Usages' },
                { key: 'smtp',     label: data.smtp?.verified ? '📧 SMTP ✓' : data.smtp ? '📧 SMTP ⚠️' : '📧 SMTP' },
                { key: 'whatsapp', label: data.settings?.whatsapp?.phone_number_id ? '💬 WA ✓' : '💬 WhatsApp' },
                { key: 'limits',   label: '⚙️ Limites' },
                { key: 'credits',  label: '💳 Crédits' },
                { key: 'actions',  label: '🔧 Actions' },
              ].map(t => (
                <button key={t.key}
                  className={`adm-modal-tab ${tab === t.key ? 'active' : ''}`}
                  onClick={() => setTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="adm-modal-body">

              {/* INFOS */}
              {tab === 'info' && (
                <div className="adm-info-grid">
                  <InfoRow label="ID" value={`#${data.id}`} />
                  <InfoRow label="Slug" value={data.slug} />
                  <InfoRow label="Plan" value={<PlanBadge plan={data.plan} />} />
                  <InfoRow label="Statut" value={<StatusPill active={data.active} />} />
                  <InfoRow label="Début plan" value={fmtDate(data.plan_starts_at)} />
                  <InfoRow label="Fin d'essai" value={fmtDate(data.trial_ends_at)} />
                  <InfoRow label="Reset usage" value={fmtDate(data.usage_reset_date)} />
                  <InfoRow label="Contacts" value={fmtNum(data.contacts_count)} />
                  <InfoRow label="Campagnes" value={fmtNum(data.campaigns_count)} />
                  <InfoRow label="Utilisateurs" value={fmtNum(data.users_count)} />
                  {data.users?.map(u => (
                    <InfoRow key={u.id} label={`👤 ${u.role}`} value={`${u.name} — ${u.email}`} />
                  ))}
                </div>
              )}

              {/* SMTP */}
              {tab === 'smtp' && (
                <div className="adm-smtp-tab">
                  {!data.smtp ? (
                    <div className="adm-smtp-none">
                      <div className="adm-smtp-none-icon">📭</div>
                      <div className="adm-smtp-none-title">Aucun SMTP configuré</div>
                      <div className="adm-smtp-none-sub">
                        Ce client n'a pas encore paramétré son serveur d'envoi.<br/>
                        Les e-mails partent via le <strong>fallback Mailjet</strong> de la plateforme.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="adm-smtp-status-row">
                        {data.smtp.verified ? (
                          <span className="adm-smtp-badge adm-smtp-badge-ok">✅ SMTP vérifié et actif</span>
                        ) : (
                          <span className="adm-smtp-badge adm-smtp-badge-warn">⚠️ SMTP configuré mais non vérifié</span>
                        )}
                        <span className="adm-smtp-fallback-note">
                          {data.smtp.verified ? 'Les e-mails passent par ce SMTP.' : '→ Fallback Mailjet actif en attendant.'}
                        </span>
                      </div>

                      <div className="adm-info-grid" style={{ marginTop: 16 }}>
                        <InfoRow label="Serveur"      value={`${data.smtp.host}:${data.smtp.port}`} />
                        <InfoRow label="Chiffrement"  value={data.smtp.encryption || 'none'} />
                        <InfoRow label="Utilisateur"  value={data.smtp.username || '—'} />
                        <InfoRow label="Expéditeur"   value={data.smtp.from_email || '—'} />
                        <InfoRow label="Nom affiché"  value={data.smtp.from_name  || '—'} />
                        <InfoRow label="Vérifié le"   value={data.smtp.verified_at ? fmtDate(data.smtp.verified_at) : '—'} />
                        <InfoRow label="Dernier test" value={data.smtp.last_tested_at ? fmtDate(data.smtp.last_tested_at) : '—'} />
                      </div>

                      {data.smtp.last_test_error && (
                        <div className="adm-smtp-error-box">
                          <div className="adm-smtp-error-title">❌ Dernière erreur SMTP</div>
                          <code className="adm-smtp-error-msg">{data.smtp.last_test_error}</code>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* WHATSAPP BUSINESS */}
              {tab === 'whatsapp' && (() => {
                const wa = data.settings?.whatsapp || {};
                const connected = !!wa.phone_number_id && !!wa.access_token;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* Statut actuel */}
                    {connected ? (
                      <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <div>
                          <div style={{ fontWeight: 700, color: '#065F46', fontSize: 14 }}>✅ WhatsApp Business connecté</div>
                          {wa.display_name && <div style={{ fontSize: 13, color: '#059669', marginTop: 2 }}>{wa.display_name}{wa.phone ? ` — ${wa.phone}` : ''}</div>}
                          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2, fontFamily: 'monospace' }}>Phone Number ID : {wa.phone_number_id}</div>
                          {wa.connected_at && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Configuré le {new Date(wa.connected_at).toLocaleDateString('fr-CH')}</div>}
                        </div>
                        <button onClick={handleDeleteWa} style={{ background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Déconnecter
                        </button>
                      </div>
                    ) : (
                      <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#92400E' }}>
                        ⚠️ WhatsApp Business non configuré pour ce client. Entrez ses identifiants Meta ci-dessous.
                      </div>
                    )}

                    {/* Formulaire */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Phone Number ID</label>
                        <input
                          type="text"
                          placeholder="ex : 123456789012345"
                          value={waForm.phone_number_id}
                          onChange={e => setWaForm(f => ({ ...f, phone_number_id: e.target.value.trim() }))}
                          style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
                        />
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                          Dans Meta for Developers → l'App du client → WhatsApp → Premiers pas → "Phone Number ID"
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                          Access Token permanent
                          {connected && <span style={{ fontWeight: 400, color: '#9CA3AF', marginLeft: 6 }}>(laisser vide pour garder l'actuel)</span>}
                        </label>
                        <input
                          type="password"
                          placeholder={connected ? '••••••••••••••••••••' : 'EAAxxxxxxxxxxxxxxxx…'}
                          value={waForm.access_token}
                          onChange={e => setWaForm(f => ({ ...f, access_token: e.target.value.trim() }))}
                          style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
                        />
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                          Token système permanent du compte Meta Business du client (pas le token 24h)
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={handleSaveWa}
                          disabled={waSaving || !waForm.phone_number_id || (!waForm.access_token && !connected)}
                          style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: (waSaving || !waForm.phone_number_id) ? 0.6 : 1 }}>
                          {waSaving ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                        {connected && (
                          <button
                            onClick={handleTestWa}
                            disabled={waTesting}
                            style={{ background: '#fff', color: '#25D366', border: '1.5px solid #25D366', borderRadius: 7, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                            {waTesting ? '⏳ Test…' : '🧪 Tester'}
                          </button>
                        )}
                      </div>

                      {waTestRes && (
                        <div style={{ padding: '9px 13px', borderRadius: 7, background: waTestRes.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${waTestRes.ok ? '#BBF7D0' : '#FECACA'}`, fontSize: 13, color: waTestRes.ok ? '#166534' : '#991B1B' }}>
                          {waTestRes.message}
                        </div>
                      )}
                    </div>

                    {/* Rappel webhook */}
                    <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 16px' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>🔗 Webhook à configurer dans Meta (une fois par client)</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
                        <div><span style={{ color: '#6B7280' }}>URL :</span> <strong>https://api.beeappee.com/api/webhooks/meta/whatsapp</strong></div>
                        <div><span style={{ color: '#6B7280' }}>Token :</span> <strong>appee-whatsapp-webhook</strong></div>
                        <div><span style={{ color: '#6B7280' }}>S'abonner à :</span> <strong>messages</strong></div>
                      </div>
                    </div>

                  </div>
                );
              })()}

              {/* USAGES */}
              {tab === 'usage' && (
                <div className="adm-usage-section">
                  <UsageBlock label="📬 E-mails" used={data.emails_used} limit={data.emails_limit} color="#3B82F6" />
                  <UsageBlock label="🤖 Crédits IA" used={data.ai_used} limit={data.ai_limit} color="#8B5CF6" />
                  <UsageBlock label="👥 Contacts" used={data.contacts_count} limit={data.contacts_limit} color="#10B981" />
                  <UsageBlock label="💬 SMS utilisés" used={Math.max(0, data.sms_limit - data.sms_credits)} limit={data.sms_limit} color="#F59E0B" />
                  <UsageBlock label="💚 WhatsApp utilisés" used={Math.max(0, data.whatsapp_limit - data.whatsapp_credits)} limit={data.whatsapp_limit} color="#25D366" />
                </div>
              )}

              {/* LIMITES */}
              {tab === 'limits' && (
                <div>
                  <p className="adm-section-hint">Modifie les limites pour ce client. Les valeurs par défaut dépendent du plan.</p>
                  <div className="adm-limits-grid">
                    <LimitInput label="Contacts max" value={limits.contacts_limit} onChange={v => setLimits(l => ({...l, contacts_limit: v}))} />
                    <LimitInput label="E-mails / mois" value={limits.emails_limit} onChange={v => setLimits(l => ({...l, emails_limit: v}))} />
                    <LimitInput label="Crédits IA / mois" value={limits.ai_limit} onChange={v => setLimits(l => ({...l, ai_limit: v}))} />
                    <LimitInput label="SMS inclus" value={limits.sms_limit} onChange={v => setLimits(l => ({...l, sms_limit: v}))} />
                    <LimitInput label="WhatsApp inclus" value={limits.whatsapp_limit} onChange={v => setLimits(l => ({...l, whatsapp_limit: v}))} />
                  </div>
                  <div className="adm-actions-row">
                    <button className="adm-btn adm-btn-primary" onClick={handleSaveLimits} disabled={saving}>
                      {saving ? <Spinner /> : null} Sauvegarder les limites
                    </button>
                    <button className="adm-btn adm-btn-ghost" onClick={() => handlePlanChange(data.plan)}>
                      ↺ Remettre aux défauts du plan
                    </button>
                  </div>

                  <div className="adm-plan-change">
                    <p className="adm-section-hint" style={{marginTop:20}}>Changer de plan :</p>
                    <div className="adm-plan-btns">
                      {Object.entries(PLAN_META).map(([key, meta]) => (
                        <button key={key}
                          className={`adm-plan-btn ${data.plan === key ? 'active' : ''}`}
                          style={{ '--plan-color': meta.color, '--plan-bg': meta.bg }}
                          onClick={() => handlePlanChange(key)}
                          disabled={saving || data.plan === key}>
                          {meta.label} — CHF {PLAN_PRICES[key]}/mo
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* CRÉDITS */}
              {tab === 'credits' && (
                <div>
                  <p className="adm-section-hint">Ajoute des crédits supplémentaires à ce client.</p>
                  <div className="adm-credits-current">
                    <CreditCard label="SMS" value={data.sms_credits} icon="💬" color="#F59E0B" />
                    <CreditCard label="WhatsApp" value={data.whatsapp_credits} icon="💚" color="#25D366" />
                    <CreditCard label="IA (limite mensuelle)" value={data.ai_limit} icon="🤖" color="#8B5CF6" />
                    <CreditCard label="E-mails (limite mensuelle)" value={data.emails_limit} icon="📧" color="#3B82F6" />
                  </div>
                  <div className="adm-credits-form">
                    <div className="adm-credit-type-row">
                      {[
                        { key: 'sms',      label: '💬 SMS',       hint: 'Crédits SMS permanents' },
                        { key: 'whatsapp', label: '💚 WhatsApp',  hint: 'Crédits WhatsApp permanents' },
                        { key: 'ai',       label: '🤖 IA',        hint: 'Augmente la limite mensuelle IA' },
                        { key: 'email',    label: '📧 E-mails',   hint: 'Augmente la limite mensuelle emails' },
                      ].map(t => (
                        <button key={t.key}
                          className={`adm-credit-type-btn ${creditType === t.key ? 'active' : ''}`}
                          onClick={() => setCreditType(t.key)}>
                          <span>{t.label}</span>
                          <span className="adm-credit-type-hint">{t.hint}</span>
                        </button>
                      ))}
                    </div>
                    <div className="adm-credit-input-row">
                      <input
                        type="number" min="1" max="100000"
                        className="adm-input"
                        placeholder="Quantité à ajouter"
                        value={creditAmount}
                        onChange={e => setCreditAmount(e.target.value)}
                      />
                      <button className="adm-btn adm-btn-primary" onClick={handleAddCredits} disabled={creditSaving || !creditAmount}>
                        {creditSaving ? <Spinner /> : '+'} Ajouter
                      </button>
                    </div>
                    <div className="adm-credit-presets">
                      {[50, 100, 500, 1000].map(v => (
                        <button key={v} className="adm-preset-btn" onClick={() => setCreditAmount(v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ACTIONS */}
              {tab === 'actions' && (
                <div className="adm-actions-section">
                  <div className="adm-action-block">
                    <h4 className="adm-action-title">↺ Réinitialiser les compteurs d'usage</h4>
                    <p className="adm-section-hint">Remet à zéro les compteurs emails_used et/ou ai_used sans toucher aux limites.</p>
                    <div className="adm-reset-row">
                      <select className="adm-select" value={resetType} onChange={e => setResetType(e.target.value)}>
                        <option value="all">Tout réinitialiser (email + IA)</option>
                        <option value="email">E-mails uniquement</option>
                        <option value="ai">IA uniquement</option>
                      </select>
                      <button className="adm-btn adm-btn-warning" onClick={handleResetUsage} disabled={resetSaving}>
                        {resetSaving ? <Spinner /> : '↺'} Réinitialiser
                      </button>
                    </div>
                  </div>

                  <div className="adm-action-block">
                    <h4 className="adm-action-title">{data.active ? '⏸ Suspendre' : '▶ Activer'} ce client</h4>
                    <p className="adm-section-hint">
                      {data.active
                        ? 'Le client ne pourra plus accéder à l\'application ni envoyer d\'e-mails.'
                        : 'Le client retrouvera l\'accès complet à son compte.'}
                    </p>
                    <button
                      className={`adm-btn ${data.active ? 'adm-btn-danger' : 'adm-btn-success'}`}
                      onClick={handleToggle}
                      disabled={saving}>
                      {saving ? <Spinner /> : null}
                      {data.active ? '⏸ Suspendre le compte' : '▶ Réactiver le compte'}
                    </button>
                  </div>

                  <div className="adm-action-block" style={{ border: '1px solid #FECACA', background: '#FEF2F2', borderRadius: 12, padding: 16 }}>
                    <h4 className="adm-action-title" style={{ color: '#991B1B' }}>🗑️ Supprimer définitivement</h4>
                    <p className="adm-section-hint">
                      Efface le compte <strong>et toutes ses données</strong> (contacts, campagnes, messages, réglages…) <strong>sans retour possible</strong>. Seule une ligne de journal reste dans l'admin. Résilie aussi l'abonnement Stripe.
                    </p>
                    {!delOpen ? (
                      <button className="adm-btn adm-btn-danger" onClick={() => setDelOpen(true)}>🗑️ Supprimer ce compte…</button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <label style={{ fontSize: 13, color: '#991B1B', fontWeight: 600 }}>
                          Pour confirmer, tape le nom exact du client : <strong>{data.name}</strong>
                        </label>
                        <input
                          className="adm-cost-edit-input"
                          placeholder={data.name}
                          value={delConfirm}
                          onChange={e => setDelConfirm(e.target.value)}
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="adm-btn" onClick={() => { setDelOpen(false); setDelConfirm(''); }} disabled={delBusy}>Annuler</button>
                          <button
                            className="adm-btn adm-btn-danger"
                            onClick={handleDelete}
                            disabled={delBusy || delConfirm.trim() !== (data.name || '').trim()}>
                            {delBusy ? <Spinner /> : '🗑️'} Supprimer définitivement
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="adm-info-row">
      <span className="adm-info-label">{label}</span>
      <span className="adm-info-value">{value}</span>
    </div>
  );
}
function UsageBlock({ label, used, limit, color, hideBar }) {
  const pct  = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const warn = pct >= 90;
  const barColor = warn ? '#EF4444' : pct >= 70 ? '#F59E0B' : color;
  return (
    <div className="adm-usage-block">
      <div className="adm-usage-block-header">
        <span className="adm-usage-label">{label}</span>
        <span className="adm-usage-numbers" style={{ color: warn ? '#EF4444' : '#374151' }}>
          {fmtNum(used)} <span className="adm-usage-sep">/</span> {fmtNum(limit)}
          <span className="adm-usage-pct" style={{ background: warn ? '#FEE2E2' : pct >= 70 ? '#FEF3C7' : '#F0FDF4', color: warn ? '#DC2626' : pct >= 70 ? '#92400E' : '#065F46' }}>
            {pct}%
          </span>
        </span>
      </div>
      {!hideBar && (
        <div className="adm-usage-bar-track">
          <div className="adm-usage-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
        </div>
      )}
    </div>
  );
}
function LimitInput({ label, value, onChange }) {
  return (
    <div className="adm-limit-field">
      <label className="adm-limit-label">{label}</label>
      <input type="number" min="0" className="adm-input" value={value ?? 0}
        onChange={e => onChange(parseInt(e.target.value) || 0)} />
    </div>
  );
}
function CreditCard({ label, value, icon, color }) {
  return (
    <div className="adm-credit-card" style={{ '--c': color }}>
      <span className="adm-credit-icon">{icon}</span>
      <span className="adm-credit-val">{fmtNum(value)}</span>
      <span className="adm-credit-label">{label}</span>
    </div>
  );
}

// ── Section Logs d'erreurs ─────────────────────────────────────────────────────
function ErrorLogsSection() {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterLevel, setFilterLevel]   = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterLevel)  params.level  = filterLevel;
      if (filterSource) params.source = filterSource;
      const res = await api.getAdminLogs(params);
      setLogs(res.logs || []);
    } finally { setLoading(false); }
  }, [filterLevel, filterSource]);

  useEffect(() => { load(); }, [load]);

  const LEVEL_COLOR = { error: '#EF4444', warning: '#F59E0B', info: '#3B82F6' };
  const LEVEL_BG    = { error: '#FEF2F2', warning: '#FFFBEB', info: '#EFF6FF' };

  return (
    <div>
      <div className="adm-logs-toolbar">
        <select className="adm-select" value={filterLevel} onChange={e => setFilterLevel(e.target.value)}>
          <option value="">Tous les niveaux</option>
          <option value="error">🔴 Erreurs</option>
          <option value="warning">🟡 Warnings</option>
          <option value="info">🔵 Info</option>
        </select>
        <select className="adm-select" value={filterSource} onChange={e => setFilterSource(e.target.value)}>
          <option value="">Toutes sources</option>
          <option value="frontend">Frontend</option>
          <option value="backend">Backend</option>
          <option value="cron">Cron</option>
          <option value="scheduler">Scheduler</option>
        </select>
        <button className="adm-btn adm-btn-ghost" onClick={load}>↻ Actualiser</button>
      </div>

      {loading ? (
        <div className="adm-loading-row"><Spinner /> Chargement des logs…</div>
      ) : logs.length === 0 ? (
        <div className="adm-empty">✅ Aucun log pour ces filtres.</div>
      ) : (
        <div className="adm-logs-list">
          {logs.map(log => (
            <div key={log.id} className="adm-log-row"
              style={{ borderLeftColor: LEVEL_COLOR[log.level], background: LEVEL_BG[log.level] }}>
              <div className="adm-log-header" onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                <span className="adm-log-level" style={{ color: LEVEL_COLOR[log.level] }}>
                  {log.level.toUpperCase()}
                </span>
                <span className="adm-log-source">[{log.source}]</span>
                {log.tenant && <span className="adm-log-tenant">{log.tenant.name}</span>}
                <span className="adm-log-msg">{log.message}</span>
                <span className="adm-log-date">{fmtDate(log.created_at)}</span>
                <span className="adm-log-chevron">{expanded === log.id ? '▲' : '▼'}</span>
              </div>
              {expanded === log.id && (
                <div className="adm-log-detail">
                  {log.url && <div className="adm-log-url">URL : {log.url}</div>}
                  {log.context && (
                    <pre className="adm-log-ctx">{JSON.stringify(log.context, null, 2)}</pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section Coûts & Rentabilité ───────────────────────────────────────────────
const SERVICE_META = {
  openai:   { icon: '🤖', label: 'OpenAI (IA)',  unit: 'appels'   },
  mailjet:  { icon: '✉️',  label: 'Mailjet',      unit: 'e-mails'  },
  sms:      { icon: '💬', label: 'SMS',          unit: 'segments' },
  whatsapp: { icon: '💚', label: 'WhatsApp',     unit: 'messages' },
};

function CostsSection() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeEdit, setActiveEdit] = useState(null); // service key being edited
  const [editForm, setEditForm]     = useState({});
  const [saving, setSaving]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getPlatformCosts()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (svc) => {
    setActiveEdit(svc.service);
    setEditForm({
      loaded_chf:            svc.loaded_chf,
      unit_cost_chf:         svc.unit_cost_chf,
      warning_threshold_chf: svc.warning_threshold_chf ?? 10,
      notes:                 svc.notes ?? '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updatePlatformCredit(activeEdit, {
        loaded_chf:            parseFloat(editForm.loaded_chf)            || 0,
        unit_cost_chf:         parseFloat(editForm.unit_cost_chf)         || 0,
        warning_threshold_chf: parseFloat(editForm.warning_threshold_chf) || 0,
        notes:                 editForm.notes || null,
      });
      setActiveEdit(null);
      await load();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="adm-costs-section"><div className="adm-loading-row"><Spinner /> Chargement des coûts…</div></div>;
  if (!data) return null;

  return (
    <div className="adm-costs-section">
      <h3 className="adm-costs-title">💸 Coûts &amp; Rentabilité</h3>

      {data.alerts && data.alerts.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.alerts.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 12,
              background: a.level === 'critical' ? '#FEF2F2' : '#FFFBEB',
              border: `1px solid ${a.level === 'critical' ? '#FECACA' : '#FDE68A'}`,
              color: a.level === 'critical' ? '#991B1B' : '#92400E', fontWeight: 600, fontSize: 13.5,
            }}>
              <span style={{ fontSize: 18 }}>{a.level === 'critical' ? '🚨' : '⚠️'}</span>
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {data.management && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          {[
            { label: 'ARPU (revenu / client)', value: `CHF ${fmtNum(data.management.arpu_chf)}` },
            { label: 'Marge nette', value: `${data.management.margin_pct}%`, hi: data.management.margin_pct >= 80 },
            { label: 'Coût / client', value: `CHF ${data.management.cost_per_client}` },
            { label: 'SMS — moy. segments', value: `${data.management.sms_avg_segments} seg/SMS`, warn: data.management.sms_avg_segments > 1.2 },
          ].map(k => (
            <div key={k.label} style={{ flex: '1 1 170px', background: '#fff', border: '1px solid #E8E5F4', borderRadius: 12, padding: '12px 16px' }}>
              <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.04em' }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: k.warn ? '#DC2626' : k.hi ? '#059669' : '#1A1535' }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="adm-costs-grid">
        {data.services.map(svc => {
          const meta    = SERVICE_META[svc.service] || {};
          const loaded  = svc.loaded_chf;
          const remaining = svc.remaining_chf;
          const pct     = loaded > 0 ? Math.max(0, Math.min(100, Math.round((remaining / loaded) * 100))) : 0;
          const barColor = pct > 50 ? '#059669' : pct > 20 ? '#F59E0B' : '#EF4444';
          const cardCls  = `adm-cost-card${svc.critical ? ' adm-cost-critical' : svc.warning ? ' adm-cost-warn' : ''}`;
          const isEditing = activeEdit === svc.service;

          return (
            <div key={svc.service} className={cardCls}>
              {/* Header */}
              <div className="adm-cost-header">
                <div className="adm-cost-icon-label">
                  <span className="adm-cost-icon">{meta.icon}</span>
                  {meta.label}
                </div>
                {svc.critical && <span className="adm-cost-badge-critical">🚨 CRITIQUE</span>}
                {!svc.critical && svc.warning && <span className="adm-cost-badge-warn">⚠️ Alerte</span>}
              </div>

              {/* Units used */}
              <div className="adm-cost-units">
                {fmtNum(svc.units_used)} {meta.unit} utilisé{svc.units_used !== 1 ? 's' : ''}
                {svc.messages != null && (
                  <span style={{ color: '#9CA3AF' }}>
                    {' '}· {fmtNum(svc.messages)} SMS ({(svc.units_used / Math.max(1, svc.messages)).toFixed(1)} seg/SMS)
                  </span>
                )}
              </div>

              {/* Amounts */}
              <div className="adm-cost-amounts">
                <span>Coût estimé&nbsp;: <strong>CHF {svc.cost_chf.toFixed(2)}</strong></span>
                <span>Budget&nbsp;: CHF {loaded.toFixed(2)}</span>
              </div>

              {/* Solde RÉEL Twilio (live, via API) — le vrai suivi anti-surprise */}
              {svc.service === 'sms' && data.twilio_balance && (
                <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 6, color: data.twilio_balance.balance < 10 ? '#DC2626' : '#5A35FF' }}>
                  💰 Solde Twilio réel&nbsp;: {data.twilio_balance.currency} {data.twilio_balance.balance.toFixed(2)}
                </div>
              )}

              {/* Progress bar */}
              {loaded > 0 ? (
                <>
                  <div className="adm-cost-bar-track">
                    <div className="adm-cost-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                  </div>
                  <div className="adm-cost-units" style={{ color: barColor, fontWeight: 700 }}>
                    CHF {remaining.toFixed(2)} restant ({pct}%)
                  </div>
                </>
              ) : (
                <div className="adm-cost-units" style={{ fontStyle: 'italic' }}>Non chargé</div>
              )}

              {/* Edit button */}
              <button className="adm-cost-edit-btn" onClick={() => isEditing ? setActiveEdit(null) : openEdit(svc)}>
                {isEditing ? 'Annuler' : 'Recharger / Modifier'}
              </button>

              {/* Inline edit form */}
              {isEditing && (
                <div className="adm-cost-edit-form">
                  <div className="adm-cost-edit-row">
                    <label className="adm-cost-edit-label">Budget rechargé (CHF)</label>
                    <input type="number" min="0" step="0.01" className="adm-cost-edit-input"
                      value={editForm.loaded_chf}
                      onChange={e => setEditForm(f => ({ ...f, loaded_chf: e.target.value }))} />
                  </div>
                  <div className="adm-cost-edit-row">
                    <label className="adm-cost-edit-label">Coût par unité (CHF)</label>
                    <input type="number" min="0" step="0.000001" className="adm-cost-edit-input"
                      value={editForm.unit_cost_chf}
                      onChange={e => setEditForm(f => ({ ...f, unit_cost_chf: e.target.value }))} />
                  </div>
                  <div className="adm-cost-edit-row">
                    <label className="adm-cost-edit-label">Seuil d'alerte (CHF)</label>
                    <input type="number" min="0" step="0.01" className="adm-cost-edit-input"
                      value={editForm.warning_threshold_chf}
                      onChange={e => setEditForm(f => ({ ...f, warning_threshold_chf: e.target.value }))} />
                  </div>
                  <div className="adm-cost-edit-row">
                    <label className="adm-cost-edit-label">Notes</label>
                    <input type="text" className="adm-cost-edit-input"
                      value={editForm.notes}
                      onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                  <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner /> : null} Sauvegarder
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* SMTP breakdown par serveur */}
      {data.smtp_breakdown && data.smtp_breakdown.length > 0 && (
        <div className="adm-smtp-breakdown">
          <div className="adm-smtp-breakdown-title">📡 Répartition des envois par serveur SMTP</div>
          <div className="adm-smtp-table">
            <div className="adm-smtp-row adm-smtp-header adm-smtp-row-5col">
              <span>Serveur</span>
              <span>Expéditeur</span>
              <span>Tenants</span>
              <span>E-mails envoyés</span>
              <span>Part</span>
            </div>
            {data.smtp_breakdown.map((row, i) => {
              const total = data.smtp_breakdown.reduce((s, r) => s + r.emails_sent, 0);
              const pct   = total > 0 ? Math.round((row.emails_sent / total) * 100) : 0;
              return (
                <div key={i} className="adm-smtp-row adm-smtp-row-5col">
                  <span className="adm-smtp-host">{row.host}</span>
                  <span className="adm-smtp-sender">
                    {row.from_email && <span className="adm-smtp-from-email">{row.from_email}</span>}
                    {row.from_name  && <span className="adm-smtp-from-name">{row.from_name}</span>}
                    {!row.from_email && '—'}
                  </span>
                  <span className="adm-smtp-num">{row.tenant_name || `${row.tenant_count} client${row.tenant_count > 1 ? 's' : ''}`}</span>
                  <span className="adm-smtp-num">{fmtNum(row.emails_sent)}</span>
                  <span className="adm-smtp-pct">
                    <div className="adm-smtp-bar-wrap">
                      <div className="adm-smtp-bar" style={{ width: `${pct}%` }} />
                    </div>
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.mailjet_breakdown && data.mailjet_breakdown.length > 0 && (
        <div className="adm-smtp-breakdown" style={{ marginTop: 16 }}>
          <div className="adm-smtp-breakdown-title">
            ✉️ Répartition des envois par Mailjet
            <span style={{ color: '#9CA3AF', fontWeight: 500, fontSize: 12, marginLeft: 6 }}>
              — {fmtNum(data.mailjet_total || 0)} e-mail{(data.mailjet_total || 0) > 1 ? 's' : ''} · {(data.mailjet_unit_chf || 0).toFixed(4)} CHF / envoi
            </span>
          </div>
          <div className="adm-smtp-table">
            <div className="adm-smtp-row adm-smtp-header adm-smtp-row-4col">
              <span>Client</span>
              <span>E-mails Mailjet</span>
              <span>Coût estimé</span>
              <span>Part</span>
            </div>
            {data.mailjet_breakdown.map((row, i) => (
              <div key={i} className="adm-smtp-row adm-smtp-row-4col">
                <span className="adm-smtp-host">{row.tenant_name}</span>
                <span className="adm-smtp-num">{fmtNum(row.emails_sent)}</span>
                <span className="adm-smtp-num">{Number(row.cost_chf).toFixed(2)} CHF</span>
                <span className="adm-smtp-pct">
                  <div className="adm-smtp-bar-wrap">
                    <div className="adm-smtp-bar" style={{ width: `${row.share_pct}%` }} />
                  </div>
                  {row.share_pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Marge par client */}
      {data.clients_margin && data.clients_margin.length > 0 && (
        <div style={{ marginTop: 18, background: '#fff', border: '1px solid #E8E5F4', borderRadius: 14, padding: '16px 18px', overflowX: 'auto' }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 12 }}>
            🏆 Marge par client <span style={{ color: '#9CA3AF', fontWeight: 500, fontSize: 12 }}>— du plus rentable au moins rentable</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6B7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                <th style={{ padding: '6px 8px' }}>Client</th>
                <th style={{ padding: '6px 8px' }}>Plan</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Revenu</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Coût</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Marge</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {data.clients_margin.map(c => {
                const pctColor = c.margin_pct >= 80 ? '#059669' : c.margin_pct >= 50 ? '#F59E0B' : '#DC2626';
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px', fontWeight: 600 }}>{c.name}</td>
                    <td style={{ padding: '8px', textTransform: 'capitalize', color: '#6B7280' }}>{c.plan}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>CHF {fmtNum(c.revenue_chf)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#6B7280' }}
                        title={`SMS ${c.cost_sms_chf.toFixed(2)} · IA ${c.cost_ai_chf.toFixed(2)} · e-mail ${c.cost_email_chf.toFixed(2)} · ${c.sms_segments} segments`}>
                      CHF {c.cost_chf.toFixed(2)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: c.margin_chf >= 0 ? '#059669' : '#DC2626' }}>CHF {c.margin_chf.toFixed(2)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: pctColor }}>{c.margin_pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Margin row */}
      <div className="adm-cost-margin-row">
        <div className="adm-cost-margin-item">
          <div className="adm-cost-margin-label">MRR</div>
          <div className="adm-cost-margin-value" style={{ color: '#111' }}>CHF {fmtNum(data.mrr_chf)}</div>
        </div>
        <div className="adm-cost-margin-divider">−</div>
        <div className="adm-cost-margin-item">
          <div className="adm-cost-margin-label">Coûts</div>
          <div className="adm-cost-margin-value" style={{ color: '#111' }}>CHF {data.total_costs_chf.toFixed(2)}</div>
        </div>
        <div className="adm-cost-margin-divider">=</div>
        <div className="adm-cost-margin-item">
          <div className="adm-cost-margin-label">Marge nette estimée</div>
          <div className={`adm-cost-margin-value ${data.net_margin_chf >= 0 ? 'adm-cost-margin-positive' : 'adm-cost-margin-negative'}`}>
            CHF {data.net_margin_chf.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Intégrations Tab ──────────────────────────────────────────────────────────
const INTEGRATION_DEFS = {
  ai: {
    icon: '🤖',
    label: 'Intelligence Artificielle',
    description: 'Génération de messages, rapports, insights dashboard',
    providers: ['OpenAI', 'Anthropic', 'Mistral', 'Azure OpenAI'],
    fields: [
      { key: 'api_key',  label: 'Clé API',    type: 'password', placeholder: 'sk-...' },
      { key: 'model',    label: 'Modèle',      type: 'text',     placeholder: 'gpt-4o-mini' },
      { key: 'base_url', label: 'URL de base', type: 'text',     placeholder: 'https://api.openai.com/v1' },
    ],
  },
  sms: {
    icon: '📱',
    label: 'SMS (Twilio)',
    description: 'Envoi de SMS alphanumériques via Twilio. Le nom de l\'expéditeur (ex: "Appee") apparaît à la place du numéro.',
    providers: ['Twilio'],
    fields: [
      { key: 'account_sid', label: 'Account SID',  type: 'password', placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
      { key: 'auth_token',  label: 'Auth Token',   type: 'password', placeholder: '••••••••••••••••••••••••••••••••' },
      { key: 'sms_number',  label: 'Expéditeur par défaut',   type: 'text',     placeholder: 'Appee (alphanum) ou +41791234567' },
    ],
  },
  whatsapp: {
    icon: '💬',
    label: 'WhatsApp Business (Meta — plateforme)',
    description: 'Credentials Meta de niveau plateforme pour le webhook. Les credentials par client sont gérés dans la fiche tenant (onglet WhatsApp).',
    providers: ['Meta Business API'],
    fields: [
      { key: 'app_id',      label: 'App ID Meta',        type: 'text',     placeholder: '123456789012345' },
      { key: 'app_secret',  label: 'App Secret',          type: 'password', placeholder: 'abc123def456…' },
      { key: 'verify_token',label: 'Webhook Verify Token',type: 'text',     placeholder: 'appee-whatsapp-webhook' },
    ],
  },
  mailjet: {
    icon: '✉️',
    label: 'Mailjet (fallback email)',
    description: "Fallback si un client n'a pas de SMTP configuré. Les envois passent par le domaine vérifié Appee.",
    providers: ['Mailjet', 'SendGrid', 'Brevo', 'Postmark'],
    fields: [
      { key: 'api_key',    label: 'Clé API',         type: 'password', placeholder: 'Votre API Key Mailjet' },
      { key: 'api_secret', label: 'Secret API',      type: 'password', placeholder: 'Votre Secret Key Mailjet' },
      { key: 'from_email', label: 'Email expéditeur', type: 'text',    placeholder: 'noreply@beeappee.com' },
      { key: 'from_name',  label: 'Nom expéditeur',  type: 'text',     placeholder: 'Appee' },
    ],
  },
  stripe: {
    icon: '💳',
    label: 'Stripe (paiements)',
    description: 'Gestion des abonnements et paiements des clients Appee.',
    providers: ['Stripe'],
    fields: [
      { key: 'publishable_key',  label: 'Clé publique',     type: 'text',     placeholder: 'pk_live_...' },
      { key: 'secret_key',       label: 'Clé secrète',      type: 'password', placeholder: 'sk_live_...' },
      { key: 'webhook_secret',   label: 'Secret webhook',   type: 'password', placeholder: 'whsec_...' },
      { key: 'price_starter',    label: 'Price ID Starter', type: 'text',     placeholder: 'price_...' },
      { key: 'price_pro',        label: 'Price ID Pro',     type: 'text',     placeholder: 'price_...' },
      { key: 'price_premium',    label: 'Price ID Premium', type: 'text',     placeholder: 'price_...' },
    ],
  },
  email_global: {
    icon: '🛡️',
    label: 'SMTP de secours (plateforme)',
    description: "Utilisé uniquement si un client n'a pas de SMTP configuré. Configurer ici un SMTP Appee comme fallback.",
    providers: ['SMTP Appee', 'Mailjet SMTP', 'SendGrid SMTP'],
    fields: [
      { key: 'host',       label: 'Serveur SMTP',     type: 'text',     placeholder: 'smtp.example.com' },
      { key: 'port',       label: 'Port',             type: 'text',     placeholder: '587' },
      { key: 'username',   label: 'Utilisateur',      type: 'text',     placeholder: 'user@example.com' },
      { key: 'password',   label: 'Mot de passe',     type: 'password', placeholder: '' },
      { key: 'from_email', label: 'Email expéditeur', type: 'text',     placeholder: 'noreply@beeappee.com' },
      { key: 'from_name',  label: 'Nom expéditeur',   type: 'text',     placeholder: 'Appee' },
    ],
  },
};

function IntegrationStatusBadge({ integration }) {
  if (!integration) return <span className="adm-integ-badge adm-integ-badge-inactive">⏸ Inactif</span>;
  const { test_status, active } = integration;
  if (!active) return <span className="adm-integ-badge adm-integ-badge-inactive">⏸ Inactif</span>;
  if (test_status === 'ok') return <span className="adm-integ-badge adm-integ-badge-ok">✅ Connecté</span>;
  if (test_status === 'error') return <span className="adm-integ-badge adm-integ-badge-error">❌ Erreur</span>;
  return <span className="adm-integ-badge adm-integ-badge-warn">⚡ Non testé</span>;
}

function IntegrationCard({ service, def, integration, onRefresh }) {
  const [expanded, setExpanded]       = useState(false);
  const [testing, setTesting]         = useState(false);
  const [testResult, setTestResult]   = useState(null);
  const [saving, setSaving]           = useState(false);
  const [provider, setProvider]       = useState(integration?.provider || def.providers[0]);
  const [active, setActive]           = useState(integration?.active ?? true);
  const [fieldValues, setFieldValues] = useState(() => {
    const masked = integration?.masked_config || {};
    const init = {};
    def.fields.forEach(f => { init[f.key] = masked[f.key] || ''; });
    return init;
  });

  // Sync state when integration data changes
  useEffect(() => {
    if (integration) {
      setProvider(integration.provider || def.providers[0]);
      setActive(integration.active ?? true);
      const masked = integration.masked_config || {};
      const init = {};
      def.fields.forEach(f => { init[f.key] = masked[f.key] || ''; });
      setFieldValues(init);
    }
  }, [integration, def]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.testIntegration(service);
      setTestResult({ ok: res.status === 'ok', msg: res.message });
      onRefresh();
    } catch (e) {
      setTestResult({ ok: false, msg: e.message });
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const config = {};
      def.fields.forEach(f => { config[f.key] = fieldValues[f.key]; });
      await api.updateIntegration(service, { provider, active, config });
      setExpanded(false);
      onRefresh();
    } catch (e) {
      console.error(e);
    } finally { setSaving(false); }
  };

  const cardCls = [
    'adm-integ-card',
    integration?.active && integration?.test_status === 'ok' ? 'adm-integ-active' : '',
    integration?.test_status === 'error' ? 'adm-integ-error' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardCls}>
      <div className="adm-integ-header">
        <div className="adm-integ-icon">{def.icon}</div>
        <div className="adm-integ-info">
          <p className="adm-integ-title">{def.label}</p>
          <p className="adm-integ-desc">{def.description}</p>
        </div>
        <div className="adm-integ-status">
          <IntegrationStatusBadge integration={integration} />
          {integration?.test_status === 'error' && integration?.test_message && (
            <span className="adm-integ-tested" style={{ color: '#991B1B' }}>{integration.test_message}</span>
          )}
          {integration?.last_tested_at && (
            <span className="adm-integ-tested">Testé {fmtDate(integration.last_tested_at)}</span>
          )}
          <div className="adm-integ-actions">
            <button className="adm-btn adm-btn-ghost adm-btn-sm"
              onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Fermer' : 'Modifier'}
            </button>
            <button className="adm-btn adm-btn-sm"
              style={{ background: '#EEE9FF', color: '#5A35FF', border: 'none' }}
              onClick={handleTest} disabled={testing}>
              {testing ? <Spinner /> : '⚡'} Tester
            </button>
          </div>
          {testResult && (
            <div className={`adm-integ-test-result ${testResult.ok ? 'adm-integ-test-ok' : 'adm-integ-test-error'}`}>
              {testResult.ok ? '✅' : '❌'} {testResult.msg}
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="adm-integ-body">
          <div className="adm-integ-provider-row">
            <span className="adm-integ-provider-label">Fournisseur</span>
            <select className="adm-integ-provider-select"
              value={provider} onChange={e => setProvider(e.target.value)}>
              {def.providers.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', marginLeft: 16 }}>
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
              Actif
            </label>
          </div>

          <div className="adm-integ-fields">
            {def.fields.map(f => (
              <div key={f.key} className="adm-integ-field">
                <label className="adm-integ-field-label">{f.label}</label>
                <input
                  type={f.type === 'password' ? 'text' : f.type}
                  className="adm-integ-field-input"
                  placeholder={fieldValues[f.key] && f.type === 'password' ? '(conserver la valeur existante)' : f.placeholder}
                  value={fieldValues[f.key]}
                  onChange={e => setFieldValues(v => ({ ...v, [f.key]: e.target.value }))}
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          <div className="adm-integ-save-row">
            <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner /> : null} Sauvegarder
            </button>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setExpanded(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function IntegrationsTab() {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading]           = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getIntegrations();
      setIntegrations(res || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="adm-loading-row"><Spinner /> Chargement des intégrations…</div>;

  return (
    <div className="adm-integrations">
      {Object.entries(INTEGRATION_DEFS).map(([service, def]) => (
        <IntegrationCard
          key={service}
          service={service}
          def={def}
          integration={integrations[service] || null}
          onRefresh={load}
        />
      ))}
    </div>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────
export default function Admin() {
  const { user } = useApp();
  const [stats, setStats]         = useState(null);
  const [tenants, setTenants]     = useState([]);
  const [statsLoading, setStatsLoading]   = useState(true);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch]       = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await api.getAdminStats()); }
    finally { setStatsLoading(false); }
  }, []);

  const fetchTenants = useCallback(async () => {
    setTenantsLoading(true);
    try {
      const res = await api.getAdminTenants();
      setTenants(Array.isArray(res) ? res : res.tenants ?? []);
    } finally { setTenantsLoading(false); }
  }, []);

  // Couper / réactiver les envois d'un client depuis l'admin.
  const toggleTenantSending = async (t, next) => {
    try {
      await api.setAdminTenantSending(t.id, next);
      setTenants(list => list.map(x => x.id === t.id ? { ...x, sending_paused: next } : x));
    } catch (err) {
      window.alert(err.message || 'Erreur');
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchStats();
    fetchTenants();
  }, [user, fetchStats, fetchTenants]);

  const handleRefresh = useCallback(() => { fetchStats(); fetchTenants(); }, [fetchStats, fetchTenants]);

  if (user?.role !== 'admin') {
    return (
      <div className="adm-403">
        <div className="adm-403-code">403</div>
        <div className="adm-403-title">Accès réservé aux administrateurs</div>
      </div>
    );
  }

  const filtered = tenants.filter(t =>
    !search || t.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="adm-page">
      {/* Header + Nav — unified dark console band */}
      <div className="adm-header">
        <div className="adm-header-left">
          <div className="adm-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
            </svg>
          </div>
          <div>
            <h1 className="adm-title">Console Admin</h1>
            <p className="adm-subtitle">Appee Platform · {tenants.length} client{tenants.length !== 1 ? 's' : ''} actifs</p>
          </div>
        </div>
        <div className="adm-header-actions">
          <button
            className={`adm-btn adm-btn-ghost${activeTab === 'logs' ? ' adm-btn-active' : ''}`}
            onClick={() => setActiveTab(activeTab === 'logs' ? 'overview' : 'logs')}
            style={{ position: 'relative' }}
          >
            {stats?.recent_errors_7d > 0
              ? <><span style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> Logs</>
              : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> Logs</>}
          </button>
          <button className="adm-btn adm-btn-ghost" onClick={handleRefresh}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Actualiser
          </button>
        </div>
      </div>

      <div className="adm-nav-encart">
        <div className="adm-tabs">
          {[
            { key: 'overview',     label: 'Vue d\'ensemble', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
            { key: 'clients',      label: `Clients (${tenants.length})`, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
            { key: 'integrations', label: 'Intégrations', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg> },
          ].map(t => (
            <button key={t.key}
              className={`adm-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div className="adm-overview">
          <PlatformStatusBar />
          {statsLoading ? (
            <div className="adm-stats-grid">{[1,2,3,4,5,6].map(i => <div key={i} className="adm-stat-card adm-skeleton" />)}</div>
          ) : stats && (
            <>
              <div className="adm-stats-grid">
                <StatCard icon="🏢" value={stats.total_tenants} label="Clients total" sub={`${stats.active_tenants} actifs · ${stats.suspended_tenants} suspendus`} />
                <StatCard icon="💰" value={`CHF ${fmtNum(stats.mrr)}`} label="MRR estimé" sub={`${stats.new_this_month} nouveau${stats.new_this_month !== 1 ? 'x' : ''} ce mois`} accent />
                <StatCard icon="👥" value={fmtNum(stats.total_contacts)} label="Contacts total" sub="toutes boutiques" />
                <StatCard icon="📧" value={fmtNum(stats.total_emails_sent)} label="E-mails envoyés" sub="cumul" />
                <StatCard icon="🤖" value={fmtNum(stats.total_ai_calls)} label="Appels IA" sub="cumul" />
                <StatCard icon="🚨" value={stats.recent_errors_7d} label="Erreurs (7j)" sub="voir onglet Logs" warn={stats.recent_errors_7d > 0} />
              </div>

              <div className="adm-overview-bottom">
                {/* Plan breakdown */}
                <div className="adm-plan-breakdown-card">
                  <h3 className="adm-card-title">Répartition des plans</h3>
                  {Object.entries(PLAN_META).map(([key, meta]) => {
                    const count = stats.plans[key] || 0;
                    const rev   = count * PLAN_PRICES[key];
                    const pct   = stats.total_tenants > 0 ? Math.round((count / stats.total_tenants) * 100) : 0;
                    return (
                      <div key={key} className="adm-plan-row">
                        <span className="adm-badge" style={{ background: meta.bg, color: meta.color, minWidth: 70 }}>{meta.label}</span>
                        <div className="adm-bar-track" style={{ flex: 1 }}>
                          <div className="adm-bar-fill" style={{ width: `${pct}%`, background: meta.color }} />
                        </div>
                        <span className="adm-plan-count">{count} clients</span>
                        <span className="adm-plan-rev">CHF {fmtNum(rev)}/mo</span>
                      </div>
                    );
                  })}
                </div>

                {/* Derniers inscrits */}
                <div className="adm-recent-card">
                  <h3 className="adm-card-title">Derniers inscrits</h3>
                  {(stats.recent_tenants || []).map(t => (
                    <div key={t.id} className="adm-recent-row"
                      onClick={() => { setActiveTab('clients'); setSelectedId(t.id); }}>
                      <span className="adm-recent-name">{t.name}</span>
                      <PlanBadge plan={t.plan} />
                      <span className="adm-recent-date">{fmtDate(t.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <CostsSection />
            </>
          )}
        </div>
      )}

      {/* ── CLIENTS ── */}
      {activeTab === 'clients' && (
        <div className="adm-clients">
          <div className="adm-clients-toolbar">
            <input className="adm-search" placeholder="🔍 Rechercher un client…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <span className="adm-count">{filtered.length} client{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {tenantsLoading ? (
            <div className="adm-table-loading">{[1,2,3,4,5].map(i => <div key={i} className="adm-row-skeleton" />)}</div>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Client</th><th>Plan</th><th>Statut</th>
                    <th className="adm-th-num">Contacts</th>
                    <th className="adm-th-num">E-mails</th>
                    <th className="adm-th-num">IA</th>
                    <th className="adm-th-num">SMS</th>
                    <th>Inscrit</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(t => (
                    <tr key={t.id} className="adm-tr" onClick={() => setSelectedId(t.id)}>
                      <td>
                        <div className="adm-tenant-name">{t.name}</div>
                        <div className="adm-tenant-slug">{t.slug}</div>
                      </td>
                      <td><PlanBadge plan={t.plan} /></td>
                      <td>
                        <StatusPill active={t.active} />
                        {t.sending_paused && (
                          <span style={{
                            display: 'inline-block', marginLeft: 6, background: 'var(--fn-danger-pale)', color: 'var(--fn-danger-deep)',
                            borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
                          }}>⏸️ Envois en pause</span>
                        )}
                      </td>
                      <td className="adm-td-num">
                        <span className={t.contacts_count >= t.contacts_limit * 0.9 ? 'adm-warn' : ''}>
                          {fmtNum(t.contacts_count)}
                        </span>
                        <span className="adm-td-limit">/{fmtNum(t.contacts_limit)}</span>
                      </td>
                      <td className="adm-td-num">
                        <span className={t.emails_used >= t.emails_limit * 0.9 ? 'adm-warn' : ''}>
                          {fmtNum(t.emails_used)}
                        </span>
                        <span className="adm-td-limit">/{fmtNum(t.emails_limit)}</span>
                      </td>
                      <td className="adm-td-num">
                        <span className={t.ai_used >= t.ai_limit * 0.9 ? 'adm-warn' : ''}>
                          {fmtNum(t.ai_used)}
                        </span>
                        <span className="adm-td-limit">/{fmtNum(t.ai_limit)}</span>
                      </td>
                      <td className="adm-td-num">{fmtNum(t.sms_credits)}</td>
                      <td className="adm-td-date">{fmtDate(t.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                          {t.sending_paused ? (
                            <button className="adm-btn adm-btn-sm"
                              style={{ background: 'var(--fn-grow)', color: '#fff', border: 'none' }}
                              onClick={e => { e.stopPropagation(); toggleTenantSending(t, false); }}>
                              ▶️ Réactiver
                            </button>
                          ) : (
                            <button className="adm-btn adm-btn-sm"
                              style={{ background: 'var(--fn-danger-pale)', color: 'var(--fn-danger-deep)', border: 'none' }}
                              onClick={e => { e.stopPropagation(); if (window.confirm(`Couper TOUS les envois (e-mails + SMS) de « ${t.name} » ?`)) toggleTenantSending(t, true); }}>
                              ⏸️ Couper
                            </button>
                          )}
                          <button className="adm-btn adm-btn-sm adm-btn-ghost"
                            onClick={e => { e.stopPropagation(); setSelectedId(t.id); }}>
                            Gérer →
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── LOGS ── */}
      {activeTab === 'logs' && <ErrorLogsSection />}

      {/* ── INTÉGRATIONS ── */}
      {activeTab === 'integrations' && <IntegrationsTab />}

      {/* Modal détail tenant */}
      {selectedId && (
        <TenantModal
          tenantId={selectedId}
          onClose={() => setSelectedId(null)}
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
}

function StatCard({ icon, value, label, sub, accent, warn }) {
  return (
    <div className={`adm-stat-card ${accent ? 'adm-stat-accent' : ''} ${warn ? 'adm-stat-warn' : ''}`}>
      <div className="adm-stat-icon">{icon}</div>
      <div className="adm-stat-value">{value}</div>
      <div className="adm-stat-label">{label}</div>
      {sub && <div className="adm-stat-sub">{sub}</div>}
    </div>
  );
}
